/**
 * NavFold — a collapsible group of nav items in the sidebar.
 *
 * Uses NavRow for the header row. When condensed, shows a popover menu on click.
 * Auto-expands when a child route is active.
 */

import React, { useState, useEffect } from 'react';
import { Typography, useTheme, Collapse, Box, Menu, List } from '@mui/material';
import { BiChevronDown, BiChevronRight } from 'react-icons/bi';
import NavRow from './NavRow';

interface NavFoldProps {
  condensed?: boolean;
  label: string;
  icon?: React.ReactElement<any>;
  /** Routes of child NavItems — used to auto-expand when a child is active */
  childRoutes?: string[];
  currentRoute?: string;
  children: React.ReactNode;
}

const NavFold: React.FC<NavFoldProps> = ({ condensed, label, icon, childRoutes, currentRoute, children }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);

  const isCondensed = condensed || false;

  // Check if any child route is active
  const hasActiveChild = !!(
    currentRoute &&
    childRoutes?.some(route =>
      route === '/' ? currentRoute === '/' : currentRoute.startsWith(route)
    )
  );

  // Auto-open when a child is active, allow manual toggle otherwise
  const [manualOpen, setManualOpen] = useState(false);
  const isOpen = hasActiveChild || manualOpen;

  // Reset manual open state when navigating away from this fold's children
  useEffect(() => {
    if (hasActiveChild) setManualOpen(false);
  }, [hasActiveChild]);

  const handleToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isCondensed) {
      setAnchorEl(anchorEl ? null : rowRef.current);
    } else {
      setManualOpen(!isOpen);
    }
  };

  const chevron = !isCondensed
    ? isOpen
      ? <BiChevronDown size={20} />
      : <BiChevronRight size={20} />
    : undefined;

  return (
    <>
      <NavRow
        ref={rowRef}
        id={`nav-fold-${label.replace(/\s+/g, '-').toLowerCase()}`}
        condensed={isCondensed}
        icon={icon}
        label={label}
        onClick={handleToggle}
        endAdornment={chevron}
      />

      {!isCondensed && (
        <Collapse in={isOpen} timeout='auto' unmountOnExit>
          <Box sx={{ pl: 2 }}>{children}</Box>
        </Collapse>
      )}

      {isCondensed && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': `nav-fold-${label.replace(/\s+/g, '-').toLowerCase()}`,
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: theme.palette.nav?.sidebar,
              color: theme.palette.text?.contrastPrimary,
              marginLeft: '8px',
            },
          }}
        >
          <List dense disablePadding sx={{ minWidth: '200px' }}>
            <Typography
              variant='caption'
              sx={{ display: 'block', p: '8px 16px', color: theme.palette.text.contrastDisabled }}
            >
              {label.toUpperCase()}
            </Typography>
            {React.Children.map(children, child => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child as React.ReactElement<any>, {
                  condensed: false,
                });
              }
              return child;
            })}
          </List>
        </Menu>
      )}
    </>
  );
};

export default NavFold;
